import React, {useContext, useEffect, useState} from 'react';
import {Badge, Button, Descriptions, InputNumber, Popconfirm, Select, Space, Table, Tag} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import IsLoading from '../../../../shared/components/IsLoading';
import DataEditor from '../../../../shared/components/dataEditor/DataEditor';
import dayjs from 'dayjs';

const OBJ_TYPE_TEXT = {
    1: '일반',
    2: '골든',
    3: '미스',
    4: '마법진',
    5: '멀티플라이',
    6: '피버',
    7: '버블',
};

const cloneSet = (node, path, idx, newValue) => {
    const key = path[idx];
    const isLast = idx === path.length - 1;

    if (Array.isArray(node)) {
        const next = node.slice();
        next[key] = isLast ? newValue : cloneSet(node[key], path, idx + 1, newValue);
        return next;
    }

    const next = {...node};
    next[key] = isLast ? newValue : cloneSet(node[key], path, idx + 1, newValue);
    return next;
};

const PlayerClaw = () => {
    const {playerData} = useContext(PlayerContext);
    const [pageData, setPageData] = useState(null);
    const [selectedEventSeq, setSelectedEventSeq] = useState(null);
    const [loading, setLoading] = useState(false);
    const [coinAmount, setCoinAmount] = useState(100);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!playerData?.userKey) return;
        fetchClawData(null);
    }, [playerData?.userKey]);

    const fetchClawData = async (eventSeq) => {
        setLoading(true);
        try {
            const params = {userKey: playerData.userKey};
            if (eventSeq !== null) params.eventSeq = eventSeq;
            const {data} = await internalApi.get('/player/claw', {params});
            setPageData(data);
            if (data.eventSeq) setSelectedEventSeq(data.eventSeq);
        } catch {
            toast.error('인형뽑기 데이터 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleEventChange = (eventSeq) => {
        setSelectedEventSeq(eventSeq);
        fetchClawData(eventSeq);
    };

    const handleAddCoin = async () => {
        if (!coinAmount || coinAmount <= 0) {
            toast.error('추가할 코인 수를 입력하세요.');
            return;
        }
        try {
            await internalApi.post('/player/claw/coin', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
                amount: coinAmount,
            });
            toast.success('코인이 추가되었습니다.');
            await fetchClawData(selectedEventSeq);
        } catch (e) {
            toast.error(e?.response?.data?.message || '코인 추가 실패');
        }
    };

    const handleReset = async () => {
        try {
            await internalApi.post('/player/claw/reset', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
            });
            toast.success('초기화되었습니다.');
            setSelectedEventSeq(null);
            await fetchClawData(null);
        } catch (e) {
            toast.error(e?.response?.data?.message || '초기화 실패');
        }
    };

    const handleObjectChange = (uid, newObjIdx) => {
        setPageData((prev) => {
            const info = prev.objectInfo?.[newObjIdx];
            const nextPool = (prev.clawData.objectPool || []).map((o) => o.uid === uid ? {
                ...o,
                objIdx: Number(newObjIdx),
                objCategory: info?.objCategory ?? o.objCategory,
                objType: info?.objType ?? o.objType,
            } : o);
            return {
                ...prev,
                clawData: {...prev.clawData, objectPool: nextPool},
            };
        });
    };

    const handleEdit = (path, newValue) => {
        setPageData((prev) => ({
            ...prev,
            clawData: path.length === 0 ? newValue : cloneSet(prev.clawData, path, 0, newValue),
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await internalApi.put('/player/claw', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
                data: pageData.clawData,
            });
            toast.success('저장되었습니다.');
            await fetchClawData(selectedEventSeq);
        } catch (e) {
            toast.error(e?.response?.data?.message || '저장 실패');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !pageData) return <IsLoading/>;

    const {eventList = [], clawData, eventMaster, objectInfo = {}} = pageData;
    const round = clawData?.round;
    const collectedTotal = round ? Object.values(round.collected || {}).reduce((s, c) => s + c, 0) : 0;

    const eventOptions = eventList.map(e => ({
        value: e.eventSeq,
        label: `이벤트 ${e.eventSeq} (${dayjs(e.updated).format('YYYY-MM-DD HH:mm')})`,
    }));

    const objectOptions = Object.entries(objectInfo)
        .map(([idx, info]) => ({
            value: Number(idx),
            label: `${info.name} (#${idx}, ${OBJ_TYPE_TEXT[info.objType] || info.objType})`,
        }))
        .sort((a, b) => a.value - b.value);

    const objectPoolColumns = [
        {title: 'UID', dataIndex: 'uid', key: 'uid', width: 80},
        {
            title: '오브젝트', key: 'objIdx', width: 260,
            render: (_, row) => (
                <Select
                    size="small"
                    showSearch
                    value={row.objIdx}
                    options={objectOptions}
                    optionFilterProp="label"
                    onChange={(v) => handleObjectChange(row.uid, v)}
                    style={{width: 240}}
                />
            ),
        },
        {
            title: '타입', dataIndex: 'objType', key: 'objType', width: 100,
            render: v => OBJ_TYPE_TEXT[v] || v,
        },
    ];

    const specialItemColumns = [
        {
            title: '종류', dataIndex: 'itemId', key: 'itemId', width: 120,
            render: v => OBJ_TYPE_TEXT[v] || v,
        },
        {title: '수량', dataIndex: 'quantity', key: 'quantity', width: 100},
    ];

    return (
        <div style={{maxWidth: 1100, margin: '0 auto', padding: 24}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20}}>
                <h2 style={{margin: 0}}>두근두근 인형 뽑기</h2>
                {eventList.length > 0 && (
                    <Select
                        value={selectedEventSeq}
                        options={eventOptions}
                        onChange={handleEventChange}
                        style={{width: 260}}
                    />
                )}
            </div>

            {!clawData ? (
                <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
                    인형 뽑기 이벤트 진행 데이터가 없습니다.
                </div>
            ) : (
                <>
                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>기본 정보</h3>
                        <Descriptions bordered size="small" column={2} style={{marginBottom: 16}}>
                            <Descriptions.Item label="이벤트 시퀀스">{clawData.seq}</Descriptions.Item>
                            <Descriptions.Item label="테이블 IDX">{clawData.loadTableIdx}</Descriptions.Item>
                            <Descriptions.Item label="코인">{clawData.coinCount}</Descriptions.Item>
                            <Descriptions.Item label="이전 배율">{clawData.prevMultiplier}</Descriptions.Item>
                            <Descriptions.Item label="라운드">{round?.roundIdx ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="라운드 목표">{collectedTotal} / {round?.goal ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="이벤트 완료 여부">
                                {clawData.isEventComplete
                                    ? <Badge status="success" text="완료"/>
                                    : <Badge status="processing" text="진행중"/>}
                            </Descriptions.Item>
                            <Descriptions.Item label="오픈 레벨">{eventMaster?.openLevel ?? '-'}</Descriptions.Item>
                        </Descriptions>

                        <Space wrap size="middle" align="end">
                            <InputNumber
                                value={coinAmount}
                                onChange={setCoinAmount}
                                min={1}
                                addonBefore="코인 추가"
                                style={{width: 200}}
                            />
                            <Button type="primary" onClick={handleAddCoin}>추가</Button>
                            <Popconfirm
                                title="정말로 초기화하시겠습니까? (데이터가 삭제되며 다음 접속 시 새로 생성됩니다)"
                                onConfirm={handleReset}
                                okText="확인"
                                cancelText="취소"
                            >
                                <Button danger>이벤트 초기화</Button>
                            </Popconfirm>
                        </Space>
                    </div>

                    <div style={{marginBottom: 24}}>
                        <h3 style={{marginBottom: 4}}>오브젝트 풀 ({(clawData.objectPool || []).length}개)</h3>
                        <div style={{marginBottom: 8, color: '#999', fontSize: 12}}>
                            오브젝트를 선택해 변경할 수 있습니다. 변경 후 하단의 저장 버튼을 눌러야 반영됩니다.
                        </div>
                        <Table
                            dataSource={clawData.objectPool || []}
                            columns={objectPoolColumns}
                            rowKey="uid"
                            pagination={{pageSize: 10}}
                            size="small"
                        />
                    </div>

                    <div style={{marginBottom: 24}}>
                        <h3 style={{marginBottom: 12}}>보유 특수 아이템</h3>
                        <Table
                            dataSource={clawData.specialItems || []}
                            columns={specialItemColumns}
                            rowKey="itemId"
                            pagination={false}
                            size="small"
                        />
                    </div>

                    <div>
                        <h3 style={{marginBottom: 12}}>
                            상세 데이터 편집 <Tag color="orange">고급</Tag>
                        </h3>
                        <DataEditor initialData={clawData} handleEdit={handleEdit}/>
                        <div style={{display: 'flex', justifyContent: 'center', marginTop: 16}}>
                            <Button type="primary" onClick={handleSave} loading={saving}>저장</Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PlayerClaw;
